import { useState } from "react"

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

import { CheckBoxMultiSelect, CheckBoxMultiSelectOptions } from "../BeeKit/CheckBoxMultiSelect"
import { Bee } from "~/src/components/BeeKit"

//region Types
type StoreFilterDesktopProps = {
  options: CheckBoxMultiSelectOptions
  selections: string[]
  onUpdate: (selections: string[]) => void
}

type StoreFilterMobileProps = StoreFilterDesktopProps & {
  close: () => void
}

type StoreFilterProps = {
  options: CheckBoxMultiSelectOptions
  selections?: string[]
  onChange: (selections: string[]) => void
}
//endregion
//
//region Component
// Expects to be inside a <MenuItems> component
function StoreFilterDesktop(props: StoreFilterDesktopProps) {
  const { options, selections, onUpdate } = props
  const optionsCount = Object.keys(options).length

  return (
    <div className="relative hidden sm:block">
      <div className="absolute top-2 z-10 w-max rounded-lg border bg-white">
        <CheckBoxMultiSelect
          options={options}
          selections={selections}
          onChange={onUpdate}
          className="flex flex-col gap-1.5 rounded-lg py-3 shadow"
        >
          {({ items, selectAll, isSelectedAll }) => (
            <>
              {optionsCount > 1 && (
                <MenuItem>
                  {({ focus }) => (
                    <label
                      className={"flex items-center gap-2 border-b px-4 pb-1.5"}
                      onClick={(e) => {
                        e.preventDefault()
                        selectAll(!isSelectedAll)
                      }}
                    >
                      <input
                        className={twMerge(
                          "accent-navy-800 pointer-events-none rounded border-gray-300 hover:accent-blue-500",
                          focus ? "ring-2 ring-blue-500" : ""
                        )}
                        type="checkbox"
                        checked={isSelectedAll}
                        onChange={() => selectAll(!isSelectedAll)}
                      />
                      All Storefronts
                    </label>
                  )}
                </MenuItem>
              )}
              {items.map(({ value, label, select, isSelected }) => {
                return (
                  <MenuItem key={value}>
                    {({ focus }) => (
                      <label
                        className={"flex items-center gap-2 px-4"}
                        onClick={(e) => {
                          e.preventDefault()
                          select(!isSelected)
                        }}
                      >
                        <input
                          className={twMerge(
                            "accent-navy-800 pointer-events-none rounded border-gray-300 hover:accent-blue-500",
                            focus ? "ring-2 ring-blue-500" : ""
                          )}
                          type="checkbox"
                          value={value}
                          checked={isSelected}
                          onChange={() => {
                            select(!isSelected)
                          }}
                        />
                        {label}
                      </label>
                    )}
                  </MenuItem>
                )
              })}
            </>
          )}
        </CheckBoxMultiSelect>
      </div>
    </div>
  )
}

// Expects to be inside a <MenuItems> component
function StoreFilterMobile(props: StoreFilterMobileProps) {
  const { options, selections: initialSelections, close, onUpdate } = props
  const [selections, setSelections] = useState<string[]>(initialSelections)

  return (
    <Bee.SelectionPanel
      className="sm:hidden"
      title="Store"
      onClose={() => {
        close()
      }}
      onApply={() => onUpdate(selections)}
      disabled={selections == initialSelections}
    >
      <CheckBoxMultiSelect
        options={options}
        selections={selections}
        onChange={(newSelections) => setSelections(newSelections)}
        selectAllText="All Storefronts"
      />
    </Bee.SelectionPanel>
  )
}

function storeFilterText(options: CheckBoxMultiSelectOptions, selections: string[]): string {
  if (selections.length == 0) {
    return "Select stores"
  } else if (selections.length == 1) {
    return options[selections[0]]
  } else if (selections.length > 1 && selections.length == Object.keys(options).length) {
    return "All Storefronts"
  } else {
    return `${selections.length} Storefronts`
  }
}

export function StoreFilter(props: StoreFilterProps) {
  const { options, selections = [], onChange } = props

  //region JSX
  return (
    <Menu>
      {({ close }) => (
        <div>
          <MenuButton
            as={Bee.Button}
            className="flex w-full items-center justify-between gap-2 font-normal"
            kind="secondary"
          >
            <span className="line-clamp-1 text-ellipsis">{storeFilterText(options, selections)}</span>
            <ChevronDownIcon className="size-4 text-gray-900" />
          </MenuButton>
          <MenuItems modal={false}>
            <StoreFilterDesktop options={options} selections={selections} onUpdate={onChange} />
            <StoreFilterMobile options={options} selections={selections} close={close} onUpdate={onChange} />
          </MenuItems>
        </div>
      )}
    </Menu>
  )
  //endregion
}
//endregion

//region Private
//endregion
